import { Button, Callout } from '@dev-spendesk/grapes';
import { type PublishedPayableRule } from '@spendesk/bookkeeping-core-types';
import { useMemo, useState } from 'react';

import { unwrapQuery } from '../../../../api/unwrapQuery';
import {
  useTranslation,
  type TGlobalFunctionTyped,
} from '../../../../common/hooks/useTranslation';
import { TypeGuardError } from '../../../../utils/switchGuard';
import {
  type EnrichedPayableRule,
  useEnrichedPayableAutomationRulesQuery,
} from '../../hooks/api/use-enriched-payable-automation-rules-query';
import { type RuleOutput } from '../../models';
import {
  AutomationConflictSelector,
  type Props as AutomationConflictSelectorProps,
} from '../AutomationConflictSelector';

export type SupportedCalloutOutputKind = keyof PublishedPayableRule['outputs'] &
  ('costCenter' | 'customField');

export type SupportedPropertyKind =
  | SupportedCalloutOutputKind
  | 'expenseCategory';

type Props<TOutput extends SupportedCalloutOutputKind> = {
  /**
   * The IDs of the payables that this callout applies to, since it can
   * support to be displayed for bulk updates.
   * If empty, the callout will not be displayed.
   */
  payableIds: string[];

  /**
   * The current value of the property on which the rule is applied.
   * This is used to display the current value in the callout.
   */
  currentValue: string;

  /**
   * onUpdate function that will be called when the user
   * clicks on the "Update with rule value" button.
   * The caller is thus responsible for updating the
   * payables with the rule value.
   */
  onUpdate: (
    output: Exclude<PublishedPayableRule['outputs'][TOutput], undefined>,
  ) => void;

  /**
   * translateOutputValue function that will be used to
   * translate the output value of the rule to some value that is suitable
   * for display in the callout.
   *
   * The provided output value is enriched.
   */
  translateOutputValue: (
    output: Exclude<EnrichedPayableRule['outputs'][TOutput], undefined>,
  ) => string;
  /**
   * The rule that is being applied, which contains the outputs that
   * can be used to set the automation property on the payables.
   */
  payableRule: PublishedPayableRule;
  /**
   * The property on which the rule is applied, since rules can have
   * multiple outputs.
   */
  appliedOnProperty: SupportedPropertyKind;

  /**
   * The rule conflict, if any, that is being displayed in the callout.
   */
  ruleConflict?: [RuleOutput, RuleOutput, ...RuleOutput[]];

  /**
   * The conflict selector properties to use.
   */
  conflictSelectorCalloutProps?: AutomationConflictSelectorProps;
};

const propertyToTranslationKey: <TOutput extends SupportedCalloutOutputKind>(
  property: Props<TOutput>['appliedOnProperty'],
) => Parameters<TGlobalFunctionTyped>[0] = (property) => {
  switch (property) {
    case 'costCenter':
      return 'expenseInbox.expenseEditor.costCenter';
    case 'customField':
      return 'expenseInbox.expenseEditor.customField';
    case 'expenseCategory':
      return 'misc.expenseCategory';
    default:
      throw new TypeGuardError(property, 'unknown property');
  }
};

const makeLocalStorageKey = (
  payableId: string,
  property: string,
  payableRuleId: string,
) =>
  `payableRuleAutomationExistsCalloutIgnore-${payableId}-${property}-${payableRuleId}`;

/**
 * PayableRuleAutomationExistsCallout is meant to be displayed for
 * payables that are on their first version and have a value applied
 * on a given property which is different from a payable rule output
 * that could apply to it.
 *
 * This means the caller should do the proper checks to ensure it should
 * render this component.
 */
export const PayableRuleAutomationExistsCallout = <
  TOutput extends SupportedCalloutOutputKind,
>({
  payableIds,
  payableRule,
  currentValue,
  translateOutputValue,
  appliedOnProperty,
  onUpdate,
  ruleConflict,
  conflictSelectorCalloutProps,
}: Props<TOutput>) => {
  const [isCalloutOpen, setIsCalloutOpen] = useState(true);
  const [isIgnored, setIsIgnored] = useState(false);
  const { t } = useTranslation();

  const chosenOutput: SupportedPropertyKind =
    appliedOnProperty === 'expenseCategory' ? 'customField' : appliedOnProperty;

  const rulesQuery = useEnrichedPayableAutomationRulesQuery([payableRule]);
  const enrichedRules = unwrapQuery(rulesQuery);
  const enrichedRule = enrichedRules?.at(0);

  const keys = payableIds.map((id) =>
    makeLocalStorageKey(id, appliedOnProperty, payableRule.id),
  );

  const atLeastOneNotIgnored = useMemo(
    () => keys.map((key) => localStorage.getItem(key)).includes(null),
    [isIgnored],
  );

  if (!enrichedRule) {
    return null;
  }

  // assume we do not want to show the rule if there is any problem with it
  const isResolvedOutputDeleted = !!enrichedRule.problems.length;

  if (
    !atLeastOneNotIgnored ||
    isResolvedOutputDeleted ||
    !payableIds ||
    !payableIds.length ||
    !payableRule.outputs[chosenOutput] ||
    !isCalloutOpen
  ) {
    return null;
  }

  const setIgnored = () => {
    keys.map((key) => localStorage.setItem(key, 'true'));

    setIsCalloutOpen(false);
    setIsIgnored(true);
  };

  const translatedValue = translateOutputValue(
    enrichedRule.outputs[chosenOutput] as Parameters<
      typeof translateOutputValue
    >[0],
  );

  const translatedProperty = t(
    propertyToTranslationKey(appliedOnProperty),
  ).toLowerCase();

  const title = t('expenseInbox.expenseEditor.matchingRuleCanBeApplied', {
    name: enrichedRule.displayName,
    kind: translatedProperty,
    currentValue,
    ruleValue: translatedValue,
  });

  const titleWhenMultipleRules = t(
    'expenseInbox.expenseEditor.matchingRuleWithMultipleOutputsCanBeApplied',
    {
      number: ruleConflict?.length,
      kind: translatedProperty,
      currentValue,
    },
  );

  if (ruleConflict && ruleConflict.length > 0 && conflictSelectorCalloutProps) {
    return (
      <div className="col-span-2 -mt-8">
        <AutomationConflictSelector
          {...conflictSelectorCalloutProps}
          customTitle={titleWhenMultipleRules}
          onSelect={(output) => {
            conflictSelectorCalloutProps.onSelect(output);
            setIsCalloutOpen(false);
          }}
        />
      </div>
    );
  }

  return (
    <Callout className="mt-4" title={title} variant="info" iconName="lightning">
      <div className="flex flex-row gap-8">
        <Button
          variant="primaryInfo"
          text="Update with rule value"
          onClick={() =>
            onUpdate(
              payableRule.outputs[chosenOutput] as Parameters<
                typeof onUpdate
              >[0],
            )
          }
        />
        <Button
          variant="secondaryNeutral"
          text="Ignore"
          label="Ignore"
          onClick={setIgnored}
        />
      </div>
    </Callout>
  );
};
